/**
 * AuditFirmHome — /audit-firm
 *
 * V98-6 / ADR-0047: a firm member's landing. Not workspace-scoped: firm
 * members hold no client membership. Track 6 review:
 *   T6-B2  a Staff card: the firm's people and "Invite staff by email" (leads).
 *   T6-B3  pending staff invitations with Accept and Decline.
 *   T6-B13 every Accept / Decline runs through busy + error handling, with an
 *          inline Alert on failure and an announced success.
 *   T6-H9  each engagement says where it stands: opens {date}, custody, closed.
 *   T6-H10 client invitations show the client, framework and period; buttons
 *          name the client.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner } from '@aigovops/design-system';
import { useAnnounce } from '../components/today/shared';
import { firmDirectoryApi, programLabel, type FirmRole, type FirmSeat } from '../lib/firm-api';
import { fmtDate, fmtRange } from '../lib/format';
import { Chip, ROLE_LABEL, errText, isLead } from './firm/firm-kit';
import './firm.css';
import './today.css';

const seatState = (s: FirmSeat): { label: string; tone: 'accent' | 'neutral' | 'success' } => {
  if (s.access === 'not_started') return { label: `Opens ${fmtDate(s.window?.starts_at ?? s.engagement_start)}`, tone: 'neutral' };
  if (s.access === 'closed') return { label: 'Closed', tone: 'neutral' };
  if (s.phase === 'custody' || s.revoked_at) return { label: 'Custody · partner/manager only', tone: 'neutral' };
  if (s.phase === 'assembly') return { label: 'Assembly · read-only', tone: 'neutral' };
  return { label: 'Fieldwork', tone: 'accent' };
};

export function AuditFirmHome() {
  const qc = useQueryClient();
  const [announcer, announce] = useAnnounce();
  const engagements = useQuery({ queryKey: ['firm-my-engagements'], queryFn: firmDirectoryApi.engagements });
  const mine = useQuery({ queryKey: ['firm-mine'], queryFn: firmDirectoryApi.mine });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<{ id: string; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['firm-mine'] }).then(() => qc.invalidateQueries({ queryKey: ['firm-my-engagements'] }));
  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    if (busy) return;
    setBusy(id);
    setErr(null);
    try {
      await fn();
      await refresh();
      announce(ok);
    } catch (e) {
      setErr({ id, text: errText(e) });
    } finally {
      setBusy(null);
    }
  };
  const leadFirms = (mine.data?.firms ?? []).filter((f) => !f.staff || isLead(f.staff.firm_role));

  return (
    <div className="gp-firm-page">
      <main className="gp-firm">
        <PageHeader title="Your engagements" subtitle={<Chip>Audit firm</Chip>} />
        {mine.error ? <Alert variant="error">{errText(mine.error)}</Alert> : null}

        {mine.data?.pending_staff.length ? (
          <Card title="Staff invitations">
            <ul className="gp-firm-list">
              {mine.data.pending_staff.map((p) => (
                <li key={p.staff_id} className="gp-firm-row">
                  <div className="gp-firm-row-head">
                    <h3 className="gp-firm-row-title">{p.firm_name}</h3>
                    {p.firm_role ? <Chip>{ROLE_LABEL[p.firm_role]}</Chip> : null}
                  </div>
                  {err?.id === p.staff_id ? <Alert variant="error">{err.text}</Alert> : null}
                  <div className="gp-firm-actions">
                    <Button compact aria-disabled={busy !== null || undefined} aria-label={`Join ${p.firm_name}`} onClick={() => void act(p.staff_id, () => firmDirectoryApi.acceptStaff(p.firm_id, p.staff_id), `You joined ${p.firm_name}`)}>Accept</Button>
                    <Button compact variant="ghost" aria-disabled={busy !== null || undefined} aria-label={`Decline ${p.firm_name}`} onClick={() => void act(p.staff_id, () => firmDirectoryApi.declineStaff(p.firm_id, p.staff_id), `Declined ${p.firm_name}`)}>Decline</Button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {mine.data?.invitations.length ? (
          <Card title="Client invitations">
            <ul className="gp-firm-list">
              {mine.data.invitations.map((i) => {
                const client = i.workspace_name ?? 'A client';
                const prog = programLabel(i.period);
                return (
                  <li key={i.invitation_id} className="gp-firm-row">
                    <div className="gp-firm-row-head">
                      <h3 className="gp-firm-row-title">{client}</h3>
                      {prog ? <Chip>{prog}</Chip> : null}
                    </div>
                    <p className="gp-firm-muted">
                      {i.period ? `Period ${fmtRange(i.period.start, i.period.end)} · ` : ''}Engagement {fmtRange(i.engagement_start, i.engagement_end)}{i.firm_name ? ` · for ${i.firm_name}` : ''}
                    </p>
                    {err?.id === i.invitation_id ? <Alert variant="error">{err.text}</Alert> : null}
                    <div className="gp-firm-actions">
                      <Button compact variant="primary" aria-disabled={busy !== null || undefined} aria-label={`Accept the ${client} engagement`} onClick={() => void act(i.invitation_id, () => firmDirectoryApi.acceptInvitation(i.invitation_id), `Engagement with ${client} accepted`)}>Accept</Button>
                      <Button compact aria-disabled={busy !== null || undefined} aria-label={`Decline the ${client} engagement`} onClick={() => void act(i.invitation_id, () => firmDirectoryApi.declineInvitation(i.invitation_id), `Engagement with ${client} declined`)}>Decline</Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        ) : null}

        <section aria-labelledby="seats-h" className="gp-firm-section">
          <h2 id="seats-h" className="gp-firm-h2">Engagements</h2>
          {engagements.isLoading ? <Spinner label="Loading" /> : engagements.error ? <Alert variant="error">{errText(engagements.error)}</Alert> : (
            engagements.data?.engagements.length ? (
              <ul className="gp-firm-list">
                {engagements.data.engagements.map((e) => {
                  const st = seatState(e);
                  const prog = programLabel(e.period);
                  const name = `${e.workspace_name}${prog ? ` · ${prog}` : ''}`;
                  return (
                    <li key={e.engagement_id} className="gp-firm-row">
                      <div className="gp-firm-row-head">
                        <h3 className="gp-firm-row-title">
                          {e.access === 'open' || e.access === undefined
                            ? <Link className="gp-firm-rowlink" to={`/w/${e.workspace_slug}/app/engagements/${e.engagement_id}/firm`}>{name}</Link>
                            : name}
                        </h3>
                        <Chip tone={st.tone}>{st.label}</Chip>
                      </div>
                      <p className="gp-firm-muted">{e.firm_name} · {ROLE_LABEL[e.firm_role]}{e.period ? ` · period ${fmtRange(e.period.start, e.period.end)}` : ` · ${fmtRange(e.engagement_start, e.engagement_end)}`}</p>
                    </li>
                  );
                })}
              </ul>
            ) : <EmptyState title="No engagements yet" description="When a client invites your firm, a partner or manager accepts it here." />
          )}
        </section>

        {leadFirms.map((f) => <StaffCard key={f.firm_id} firmId={f.firm_id} firmName={f.name} announce={announce} />)}
        {announcer}
      </main>
    </div>
  );
}

/** T6-B2: the firm's people, and "Invite staff by email" for partners and managers. */
function StaffCard({ firmId, firmName, announce }: { firmId: string; firmName: string; announce: (m: string) => void }) {
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ['firm-staff', firmId], queryFn: () => firmDirectoryApi.staff(firmId) });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<FirmRole>('staff');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await firmDirectoryApi.addStaff(firmId, { email: email.trim(), firm_role: role });
      await qc.invalidateQueries({ queryKey: ['firm-staff', firmId] });
      announce(`${r.staff.display_name ?? email.trim()} invited${r.email_delivery === 'sent' ? ' · email sent' : ''}`);
      setEmail(''); setTried(false);
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  const rows = (staff.data?.staff ?? []).filter((s) => !s.removed_at);
  return (
    <Card title={`Staff · ${firmName}`}>
      <div id="staff" className="gp-firm-stack">
        {staff.isLoading ? <Spinner label="Loading" /> : staff.error ? <Alert variant="error">{errText(staff.error)}</Alert> : (
          <ul className="gp-firm-list">
            {rows.map((s) => (
              <li key={s.staff_id} className="gp-firm-actions">
                <strong>{s.display_name}</strong>
                <span className="gp-firm-muted">{s.email}</span>
                <Chip>{ROLE_LABEL[s.firm_role]}</Chip>
                {s.accepted_at ? null : <Chip tone="dashed">Invited</Chip>}
              </li>
            ))}
          </ul>
        )}
        <form className="gp-firm-form gp-firm-form--row" onSubmit={invite} noValidate aria-label="Invite staff by email">
          {err ? <Alert variant="error">{err}</Alert> : null}
          <Field id={`staff-email-${firmId}`} label="Invite staff by email" hint="They need a GAC Pro sign-in; they accept on this page." error={tried && !valid ? 'Enter an email address.' : null}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </Field>
          <Field id={`staff-role-${firmId}`} label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as FirmRole)}>
              {(['partner', 'manager', 'senior', 'staff'] as FirmRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </Select>
          </Field>
          <div className="gp-firm-actions"><Button type="submit" aria-disabled={busy || undefined}>Invite</Button></div>
        </form>
      </div>
    </Card>
  );
}
